import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import { chunkString, formatUtcTime } from "@poscon/shared-types";
import { useDispatch } from "react-redux";
import { backgroundOption, borderOption, brightOption, counterOption, fontOption, linesOption, } from "../viewOptionFactory.js";
import { eramFontNameMap, eramTextDimensionMap, getBitmapTextStyles } from "../../eramFont.js";
import { useViewOptions } from "../contexts/viewOptionContext.js";
import { useRootSelector } from "../stateSelectors.js";
import { advisoryMapSelector, selectedAdvisorySelector, selectedAdvViewSelector, setSelectedAdvisory, } from "../slices/commonSlice.js";
import { colorNameMap } from "../../colorNameMap.js";
import { computeColor } from "../../computeColor.js";
import { ViewItem } from "./ViewItem.js";
import { createItemOption, ViewItemOptionContainer } from "./ViewItemOptionContainer.js";
import { advCreateWidth, advCreationTemplateView } from "./AdvCreateTemplate.js";
import { Line } from "./Line.js";
import { advUplinkTemplateView, advUplinkTemplateWidth } from "./AdvUplinkTemplate.js";
import { advDeleteView, advDeleteWidth } from "./AdvDeletePrompt.js";
import { advFreetextTemplateView, advFreetextWidth } from "./AdvFreetextTemplate.js";
import { useViewOptionSelected } from "../hooks/useViewOptionSelected";
export const cpdlcAdvView = "CPDLC_ADV";
export function createCpdlcAdvViewMap(components) {
    return {
        [advCreationTemplateView]: components.creation,
        [advUplinkTemplateView]: components.uplink,
        [advDeleteView]: components.delete,
        [advFreetextTemplateView]: components.freetext,
    };
}
const optionMap = {
    background: backgroundOption(cpdlcAdvView),
    border: borderOption(cpdlcAdvView),
    lines: linesOption(cpdlcAdvView, 5, 20),
    font: fontOption(cpdlcAdvView),
    bright: brightOption(cpdlcAdvView),
    msgWidth: counterOption(cpdlcAdvView, "msgWidth", "MSG WIDTH", 25, 50, 12),
};
export const initialCpdlcAdvOptions = {
    lines: 5,
    msgWidth: 40,
    background: true,
    bright: 80,
    font: 2,
    border: true,
};
function colDimensionFactory(fontSize, _msgWidth) {
    const fontDim = eramTextDimensionMap[fontSize];
    const nameX = 4;
    const nameWidth = 24 * fontDim.width + 4;
    const timeX = nameX + nameWidth + 4;
    const timeWidth = 9 * fontDim.width + 4;
    const msgX = timeX + timeWidth + 4;
    const msgWidth = _msgWidth * fontDim.width + 4;
    return {
        nameX,
        nameWidth,
        timeX,
        timeWidth,
        msgX,
        msgWidth,
    };
}
const AdvRow = ({ adv, y = 0, font, alpha, msgWidth, setViewPosition }) => {
    const dispatch = useDispatch();
    const ref = useRef(null);
    const { selected, toggleViewOption } = useViewOptionSelected(`${cpdlcAdvView}/${adv.name}`);
    const selectedAdv = useRootSelector(selectedAdvisorySelector);
    const selectedAdvOption = useRootSelector(selectedAdvViewSelector);
    const colDim = colDimensionFactory(font, msgWidth);
    const tint = selected ? 0 : computeColor(colorNameMap.lightGrey, alpha);
    const fontFamily = eramFontNameMap[font];
    const fontDim = eramTextDimensionMap[font];
    const time = `${formatUtcTime(adv.createdAt)}/${adv.createdAt !== adv.updatedAt ? formatUtcTime(adv.updatedAt) : ""}`;
    const msgRows = chunkString(adv.message, msgWidth, true);
    const width = colDim.msgX + colDim.msgWidth - colDim.nameX + 4;
    const height = 4 + fontDim.height * msgRows.length;
    const options = [
        createItemOption("UPLINK", () => {
            if (ref.current) {
                const rowPos = ref.current.getGlobalPosition();
                const rect = ref.current.getBounds();
                setViewPosition(advUplinkTemplateView, {
                    x: rowPos.x + rect.width - advUplinkTemplateWidth - 1,
                    y: rowPos.y + rect.height + 4,
                });
            }
            dispatch(setSelectedAdvisory({ name: adv.name, option: advUplinkTemplateView }));
        }, {
            bgColor: selectedAdv?.name === adv.name && selectedAdvOption?.option === advUplinkTemplateView
                ? colorNameMap.brown
                : undefined,
        }),
        createItemOption("EDIT", () => {
            if (ref.current) {
                const rowPos = ref.current.getGlobalPosition();
                const rect = ref.current.getBounds();
                setViewPosition(advCreationTemplateView, {
                    x: rowPos.x + rect.width - advCreateWidth - 1,
                    y: rowPos.y + rect.height + 4,
                });
            }
            dispatch(setSelectedAdvisory({ name: adv.name, option: advCreationTemplateView }));
        }, {
            bgColor: selectedAdv?.name === adv.name && selectedAdvOption?.option === advCreationTemplateView
                ? colorNameMap.brown
                : undefined,
        }),
        createItemOption("DELETE", () => {
            if (ref.current) {
                const rowPos = ref.current.getGlobalPosition();
                const rect = ref.current.getBounds();
                setViewPosition(advDeleteView, {
                    x: rowPos.x + rect.width - advDeleteWidth - 1,
                    y: rowPos.y + rect.height + 4,
                });
            }
            dispatch(setSelectedAdvisory({ name: adv.name, option: advDeleteView }));
        }, {
            bgColor: selectedAdv?.name === adv.name && selectedAdvOption?.option === advDeleteView
                ? colorNameMap.brown
                : undefined,
        }),
    ];
    return (_jsxs("container", { y: y, children: [_jsxs(ViewItem, { ref: ref, eventMode: "static", width: width, height: height, fillColor: selected ? computeColor(colorNameMap.lightGrey, alpha) : 0, onmousedown: toggleViewOption, children: [_jsx("bitmapText", { eventMode: "none", text: adv.name + (adv.sectorCreatedShort ? `(${adv.sectorCreatedShort})` : ""), x: colDim.nameX, y: 3, style: { ...getBitmapTextStyles(fontFamily), fill: tint } }), _jsx("bitmapText", { eventMode: "none", text: time, x: colDim.timeX, y: 3, style: { ...getBitmapTextStyles(fontFamily), fill: tint } }), _jsx("bitmapText", { eventMode: "none", text: msgRows.join("\n"), x: colDim.msgX, y: 3, style: { ...getBitmapTextStyles(fontFamily), fill: tint } })] }), selected && _jsx(ViewItemOptionContainer, { xOffset: width, closeOnClickOutside: false, options: options })] }));
};
export const CpdlcAdv = ({ pos, ViewComponent, setViewPosition }) => {
    const dispatch = useDispatch();
    const viewOptions = useViewOptions();
    const advisoryMap = useRootSelector(advisoryMapSelector);
    const { font, bright, msgWidth } = viewOptions;
    const fontName = eramFontNameMap[font];
    const fontDim = eramTextDimensionMap[font];
    const alpha = bright / 100;
    const tint = computeColor(colorNameMap.white, alpha);
    const headerButtons = [
        {
            text: "FREETEXT...",
            onmousedown: () => {
                setViewPosition(advFreetextTemplateView, {
                    x: pos.x + (37 + msgWidth - 9) * fontDim.width - advFreetextWidth - 4,
                    y: pos.y + fontDim.height + 6,
                });
                dispatch(setSelectedAdvisory({ option: advFreetextTemplateView }));
            },
        },
        {
            text: "CREATE...",
            onmousedown: () => {
                setViewPosition(advCreationTemplateView, {
                    x: pos.x + (37 + msgWidth - 1) * fontDim.width - advCreateWidth - 2,
                    y: pos.y + fontDim.height + 6,
                });
                dispatch(setSelectedAdvisory({ option: advCreationTemplateView }));
            },
        },
    ];
    const colDim = colDimensionFactory(font, msgWidth);
    const advisories = Object.values(advisoryMap);
    const height = fontDim.height +
        5 +
        (advisories.length > 0
            ? advisories.reduce((acc, { message }) => {
                return acc + fontDim.height * Math.ceil(chunkString(message, msgWidth).length) + 4;
            }, 4) + 4
            : 0);
    let rowY = 4;
    return (_jsxs(ViewComponent, { view: cpdlcAdvView, optionMap: optionMap, width: 37 + msgWidth, height: height, headerButtons: headerButtons, children: [_jsx(ViewItem, { text: "NAME", x: 4 + colDim.nameX, y: 1, fontName: fontName, tint: tint, eventMode: "none" }), _jsx(ViewItem, { text: "TIME(C/U)", x: 4 + colDim.timeX, y: 1, fontName: fontName, tint: tint, eventMode: "none" }), _jsx(ViewItem, { text: "MESSAGE CONTENT", x: 4 + colDim.msgX, y: 1, fontName: fontName, tint: tint, eventMode: "none" }), _jsx(Line, { length: (37 + msgWidth) * fontDim.width - 1, y: fontDim.height + 4, tint: colorNameMap.darkGrey }), advisories.length > 0 && (_jsx("container", { x: 4, y: fontDim.height + 4, children: advisories.map((adv) => {
                    const y = rowY;
                    rowY += fontDim.height * Math.ceil(chunkString(adv.message, msgWidth).length) + 4;
                    return (_jsx(AdvRow, { y: y, adv: adv, font: font, alpha: alpha, msgWidth: msgWidth, setViewPosition: setViewPosition }, adv.name));
                }) }))] }));
};
//# sourceMappingURL=CpdlcAdv.js.map